import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    setSceneWatermarkOverrideOperation,
    setSceneWatermarkPositionOperation,
} from 'js/src/luminary/luminary-operations';

export const setWatermarkSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    visibility: z
        .boolean()
        .optional()
        .describe(
            'The visibility of the watermark. Provide this to change watermark visibility (show/hide).',
        ),
    watermark_source_index: z
        .int()
        .optional()
        .describe(
            'The index of the watermark to apply as watermark. Must be an index from the template watermark sources.',
        ),
    position: z
        .enum(['top_left', 'top_right', 'bottom_left', 'bottom_right'])
        .optional()
        .describe(
            'The position where to place the watermark. Provide this to change watermark position.',
        ),
});
export type SetWatermarkArgs = z.infer<typeof setWatermarkSchema>;

export class SetWatermarkTool extends VideoStateTool<typeof setWatermarkSchema> {
    protected override readonly schema = setWatermarkSchema;
    protected override readonly toolName = TOOL_NAMES.setWatermark;
    protected override readonly toolDescription =
        'Set watermark for a specific scene. To modify the watermark, use the watermark_source_index to change its source, the position parameter to adjust its placement, or the visibility parameter to alter its visibility.';

    public override getToolDisplayName(): string {
        return 'Setting watermark';
    }

    public get statusIcon(): string {
        return 'branding_watermark';
    }

    public async execute(args: SetWatermarkArgs, context: ToolExecutionContext) {
        const { watermark_source_index, position, visibility } = args;

        // check if the user has the watermark capability
        const canSetWatermark = SERVER?.USER?.capabilities?.custom_watermark ?? false;
        if (!canSetWatermark) {
            return `Error: Current plan does not support custom watermarks. Use ${TOOL_NAMES.suggestUpgrade} tool to show the user that they need to upgrade.`;
        }

        const changes: string[] = [];
        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];

            // Determine operation based on provided parameters
            if (watermark_source_index !== undefined && position && visibility !== undefined) {
                return 'Error: Please provide either visibility, watermark_source_index or position, not everything at once';
            }

            if (watermark_source_index === undefined && !position && visibility === undefined) {
                return 'Error: Please provide either visibility (to change hide or show the watermark), watermark_source_index (to change watermark source) or position (to change watermark position)';
            }

            // NOTE: the watermark is a special asset which is part of a scene design. If the scene design does not have a watermark, we cannot set it.
            if (!scene.watermark) {
                return `Error: No watermark available for editing on scene ${sceneShortId}.`;
            }

            const { parentActionGroup } = context;
            const sceneId = scene.id;

            // Set up operations
            const { setSceneWatermarkOverride, setSceneWatermarkPosition } = makeOperationsCallable(
                {
                    setSceneWatermarkOverrideOperation,
                    setSceneWatermarkPositionOperation,
                },
                this.luminary,
                this.dispatch,
                () => null as unknown as RootState,
            );

            if (visibility !== undefined) {
                setSceneWatermarkOverride(sceneId, visibility ? 0 : null, parentActionGroup);

                changes.push('watermark visibility');
            }
            if (watermark_source_index !== undefined) {
                if (
                    watermark_source_index < 0 ||
                    watermark_source_index > this.luminary.template.watermarkSources.length
                ) {
                    return `Error: watermark is out of range ${watermark_source_index}`;
                }

                setSceneWatermarkOverride(sceneId, watermark_source_index, parentActionGroup);

                changes.push('watermark source index');
            }
            if (position) {
                if (!scene.watermark.availablePositions.includes(position)) {
                    return `Error: Invalid position "${position}". Available positions are: ${scene.watermark.availablePositions.join(', ')}`;
                }
                setSceneWatermarkPosition(sceneId, position, parentActionGroup);

                changes.push('watermark position');
            }
            return `Success: Updated ${changes.join(', ')} for watermark in scene ${sceneShortId}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting watermark: ${errorMessage}`;
        }
    }
}
